import React, {
  useEffect,
  useState,
} from "react";

import {
  Avatar,
  Popover,
  Tooltip,
} from "antd";

import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LogoutOutlined,
} from "@ant-design/icons";

import CriticalOperationService from "../services/CriticalOperationService";

const MOBILE_BREAKPOINT = 768;

const HeaderBar = ({
  collapsed,
  toggleSidebar,
  userData,
  onLogout,
}) => {
  const [siteTitle, setSiteTitle] = useState(
    "SHAHI EXPORTS PVT. LTD, UNIT-42"
  );

  const [loading, setLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= MOBILE_BREAKPOINT
      : false
  );

  useEffect(() => {
    const fetchSiteTitle = async () => {
      setLoading(true);

      try {
        const res =
          await CriticalOperationService.getSiteTitle();

        const body = res?.data;

        if (
          res?.ok &&
          body?.status &&
          body?.title
        ) {
          setSiteTitle(String(body.title));
        }
      } catch (err) {
        console.error(
          "Error fetching site title:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSiteTitle();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        window.innerWidth <= MOBILE_BREAKPOINT
      );
    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  useEffect(() => {
    if (isMobile && !collapsed) {
      setProfileOpen(false);
      toggleSidebar?.();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Keep floating UI in sync with the shell transition.
  // If the sidebar changes width while the Popover is open,
  // Ant Design can briefly keep the previous popup position.
  useEffect(() => {
    setProfileOpen(false);
  }, [collapsed]);

  const handleSidebarToggle = () => {
    setProfileOpen(false);

    // Let React commit the closed popover before the sider starts
    // its width animation. This prevents the popup from visually
    // dragging/jumping while the layout is transitioning.
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        toggleSidebar?.();
      });
      return;
    }

    toggleSidebar?.();
  };

  const handleLogout = () => {
    setProfileOpen(false);
    onLogout?.();
  };

  const displayName =
    userData?.full_name ||
    userData?.username ||
    "User";

  const username =
    userData?.username || "";

  const userInitial =
    String(displayName)
      .trim()
      .charAt(0)
      .toUpperCase() || "U";

  const showUsernameBelowName =
    username &&
    username.toLowerCase() !==
      String(displayName).toLowerCase();

  const popContent = (
    <div className="smartapp-account-menu">
      <div className="smartapp-account-profile">
        <Avatar
          size={40}
          className="smartapp-account-avatar"
        >
          {userInitial}
        </Avatar>

        <div className="smartapp-account-profile-copy">
          <strong className="smartapp-account-name">
            {displayName}
          </strong>

          {showUsernameBelowName && (
            <span className="smartapp-account-username">
              @{username}
            </span>
          )}
        </div>
      </div>

      <div className="smartapp-account-divider" />

      <div className="smartapp-account-details">
        <div className="smartapp-account-detail-row">
          <span className="smartapp-account-detail-label">
            User ID
          </span>

          <span className="smartapp-account-detail-value">
            {userData?.su_id ?? "-"}
          </span>
        </div>

        <div className="smartapp-account-detail-row">
          <span className="smartapp-account-detail-label">
            Username
          </span>

          <span className="smartapp-account-detail-value">
            {username || "-"}
          </span>
        </div>
      </div>

      <div className="smartapp-account-divider" />

      <button
        type="button"
        className="smartapp-account-logout"
        onClick={() => {
          setProfileOpen(false);
          onLogout?.();
        }}
      >
        <span className="smartapp-account-logout-icon">
          <LogoutOutlined />
        </span>

        <span>Logout</span>
      </button>
    </div>
  );

  return (
    <header className="smartapp-header">
      <div className="smartapp-header-left">
        <Tooltip
          title={
            collapsed
              ? "Open sidebar"
              : "Close sidebar"
          }
        >
          <button
            type="button"
            onClick={handleSidebarToggle}
            aria-label={
              collapsed
                ? "Open sidebar"
                : "Close sidebar"
            }
            className="smartapp-sidebar-toggle"
          >
            {collapsed ? (
              <MenuUnfoldOutlined />
            ) : (
              <MenuFoldOutlined />
            )}
          </button>
        </Tooltip>

        <div className="smartapp-header-title-wrap">
          <span className="smartapp-header-eyebrow">
            SM-ART Operations
          </span>

          <h1
            title={loading ? "Loading..." : siteTitle}
            className="smartapp-header-title"
          >
            {loading ? "Loading..." : siteTitle}
          </h1>
        </div>
      </div>

      <div className="smartapp-header-actions">
        <Popover
          placement="bottomRight"
          content={popContent}
          trigger="click"
          open={profileOpen}
          onOpenChange={setProfileOpen}
          arrow={false}
          align={{
            offset: [0, 4],
          }}
          rootClassName="smartapp-user-popover-overlay"
        >
          <button
            type="button"
            className={
              profileOpen
                ? "smartapp-user-trigger is-open"
                : "smartapp-user-trigger"
            }
            aria-label="Open user menu"
            aria-expanded={profileOpen}
          >
            <Avatar
              size={34}
              className="smartapp-header-avatar"
            >
              {userInitial}
            </Avatar>

            {!isMobile && (
              <div className="smartapp-header-user-copy">
                <strong>{displayName}</strong>
              </div>
            )}
          </button>
        </Popover>
      </div>
    </header>
  );
};

export default HeaderBar;
